// src/pages/CategoryPage.jsx
import React from 'react';
import { useApp } from '../context/AppContext';
import ProductCard from '../components/ProductCard';
import { useNavigate, useLocation } from 'react-router-dom';
import { categoryStructure } from '../components/Header';

// URL에서 카테고리 정보 추출
const getCategoryInfoFromPath = (pathname) => {
  const path = pathname.replace(/^\/+|\/+$/g, ''); // 양 끝 슬래시 제거
  
  if (path === 'products') return { type: 'view', view: 'all' };
  if (path === 'latest') return { type: 'view', view: 'latest' };
  if (path === 'popular') return { type: 'view', view: 'popular' };
  if (path === 'recommended') return { type: 'view', view: 'recommended' };
  
  if (path.startsWith('category/')) {
    const mainKey = path.replace('category/', '');
    const main = Object.keys(categoryStructure).find(key => 
      key.toLowerCase().replace(/\s+/g, '-') === mainKey
    );
    return main ? { type: 'main', main } : null;
  }
  
  if (path.startsWith('subcategory/')) {
    const parts = path.replace('subcategory/', '').split('/');
    if (parts.length >= 2) {
      const mainKey = parts[0];
      const subKey = parts.slice(1).join('-');
      
      const main = Object.keys(categoryStructure).find(key => 
        key.toLowerCase().replace(/\s+/g, '-') === mainKey
      );
      
      if (main) {
        const sub = categoryStructure[main].find(s => 
          s.toLowerCase().replace(/\s+/g, '-').replace(/[&/]/g, '') === subKey
        );
        return sub ? { type: 'sub', main, sub } : null;
      }
    }
  }
  
  return null;
};

// 정렬 헬퍼
const sortProducts = (products, viewMode) => {
  let sorted = [...products];
  
  if (viewMode === 'latest') {
    sorted.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  } else if (viewMode === 'popular') {
    sorted.sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0));
  } else if (viewMode === 'recommended') {
    sorted.sort((a, b) => {
      const aRec = a.isRecommended ? 1 : 0;
      const bRec = b.isRecommended ? 1 : 0;
      if (bRec !== aRec) return bRec - aRec;
      return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
    });
  }
  
  return sorted;
};

const CategoryPage = () => {
  const { products } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  
  // 안전한 products 접근
  const safeProducts = products || [];
  
  const categoryInfo = getCategoryInfoFromPath(location.pathname);
  
  let filteredProducts = [];
  let pageTitle = 'All Products';
  let error = null;

  try {
    if (!categoryInfo) {
      filteredProducts = safeProducts;
    } else if (categoryInfo.type === 'view') {
      filteredProducts = sortProducts(safeProducts, categoryInfo.view);
      pageTitle = 
        categoryInfo.view === 'all' ? 'All Products' :
        categoryInfo.view === 'latest' ? 'Latest Products' :
        categoryInfo.view === 'popular' ? 'Popular Products' :
        'Recommended Products';
    } else if (categoryInfo.type === 'main') {
      filteredProducts = safeProducts.filter(p => p.mainCategory === categoryInfo.main);
      pageTitle = `${categoryInfo.main} Products`;
    } else if (categoryInfo.type === 'sub') {
      filteredProducts = safeProducts.filter(p => 
        p.mainCategory === categoryInfo.main && 
        p.subCategory === categoryInfo.sub
      );
      pageTitle = `${categoryInfo.main} > ${categoryInfo.sub}`;
    }
  } catch (err) {
    console.error('Error filtering products:', err);
    error = 'Failed to load products';
    filteredProducts = [];
  }

  return (
    <div className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="animate-fade-in">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-subheading-sm sm:text-subheading font-sf-pro text-apple-text-primary tracking-tight">
            {pageTitle}
          </h1>
          <button
            onClick={() => navigate('/')}
            className="text-caption text-apple-text-secondary hover:text-apple-text-primary apple-hover"
          >
            ← Back to Home
          </button>
        </div>

        {error ? (
          <div className="text-center py-16">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-4">
              <h2 className="text-lg font-semibold text-red-800 mb-2">Error Loading Products</h2>
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-body-sm text-apple-text-secondary">No products available.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 md:gap-5">
            {filteredProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;